import { Box, Typography, Switch } from "@mui/material";
import { useState, useEffect } from "react";
import * as React from 'react'
import { apiConstants, postGeneric } from '../../common/constants'
import Confirm from './confirm';
import { useSnackbar } from "notistack";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER, PRIMARY_BLUE } from '../../styles/foundation/colors';

const CHANNEL_LABELS = { WOO_COMMERCE: 'WooCommerce', SHOPIFY: 'Shopify', MAGENTO: 'Magento' };
const CHANNELS = ['WOO_COMMERCE', 'SHOPIFY', 'MAGENTO'];
const PAYMENT_SEGMENTS = [
    { value: 'BOTH', label: 'Both' },
    { value: 'COD', label: 'COD' },
    { value: 'PREPAID', label: 'Prepaid' },
];

// Inline payment-mode segmented control (Both / COD / Prepaid). Replaces the merchant
// panel's hard-coded "BOTH": the merchant now picks the payment mode per channel.
const PaymentSegmented = ({ value, onChange }) => (
    <Box sx={{ display: 'inline-flex', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
        {PAYMENT_SEGMENTS.map((seg) => {
            const active = value === seg.value;
            return (
                <Box
                    key={seg.value}
                    onClick={() => !active && onChange(seg.value)}
                    sx={{
                        px: 1.25,
                        py: 0.4,
                        fontFamily: FONT,
                        fontSize: '11px',
                        cursor: active ? 'default' : 'pointer',
                        bgcolor: active ? PRIMARY_BLUE : 'transparent',
                        color: active ? '#fff' : SUBTLE,
                        fontWeight: active ? WEIGHT.semibold : WEIGHT.regular,
                        userSelect: 'none',
                    }}
                >
                    {seg.label}
                </Box>
            );
        })}
    </Box>
);

// Shared card for a channel-scoped WhatsApp config (Order Confirmation / Address Verification).
// Each channel row carries its payment-mode control, live price, and enable toggle inline.
// Enabling opens the price-forward Confirm dialog so the charge is explicit.
const ChannelConfigCard = ({ merchantId, setReload, data, pricingType, endpoint, configType, title, description, unitLabel }) => {
    const [open, setOpen] = useState(false);
    const [pendingChannel, setPendingChannel] = useState('');
    const [price, setPrice] = useState(null);
    const [paymentModes, setPaymentModes] = useState({ WOO_COMMERCE: 'BOTH', SHOPIFY: 'BOTH', MAGENTO: 'BOTH' });
    const { enqueueSnackbar } = useSnackbar();

    const configRow = data && data?.find(item => item.pricingType === pricingType);
    const channelOf = (ch) => configRow?.channelOrders?.find(item => item.channel === ch);
    const isChannelActive = (ch) => channelOf(ch)?.isActive;
    const anyActive = CHANNELS.some((ch) => isChannelActive(ch));

    // Seed each channel's payment mode from the backend (falls back to BOTH).
    useEffect(() => {
        setPrice(configRow?.configValue ? configRow?.configValue : configRow?.defaultPriceValue);
        setPaymentModes({
            WOO_COMMERCE: channelOf('WOO_COMMERCE')?.paymentMode || 'BOTH',
            SHOPIFY: channelOf('SHOPIFY')?.paymentMode || 'BOTH',
            MAGENTO: channelOf('MAGENTO')?.paymentMode || 'BOTH',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const getDefaultPrice = () => configRow?.defaultPriceValue || '0';
    const hasConfigValue = () =>
        configRow?.configValue !== '0.0' && configRow?.configValue !== '0' && configRow?.configValue !== null;

    const submit = (ch, status, paymentMode) => {
        postGeneric(`${endpoint}/${merchantId}`, { status, extChannels: ch, paymentMode }).then(() => {
            enqueueSnackbar("Success", { variant: 'success' });
            setReload(prev => prev + 1);
        }).catch((err) => {
            enqueueSnackbar("Some error occurred", { variant: 'error' });
            console.log(err);
        });
    };

    // Changing the payment mode of an already-active channel applies immediately; for an
    // inactive channel it just stages the mode used when the merchant enables it.
    const handlePaymentChange = (ch, mode) => {
        setPaymentModes((prev) => ({ ...prev, [ch]: mode }));
        if (isChannelActive(ch)) submit(ch, true, mode);
    };

    const onToggle = (ch) => {
        setPendingChannel(ch);
        setOpen(true);
    };

    // Enabling/disabling confirmed via the price-forward Confirm dialog.
    const handleConfirm = () => {
        const ch = pendingChannel;
        submit(ch, !isChannelActive(ch), paymentModes[ch]);
        setOpen(false);
    };

    return (
        <>
            <Confirm
                openDialog={open}
                setOpenDialog={setOpen}
                isActive={isChannelActive(pendingChannel)}
                title={`${title} for ${CHANNEL_LABELS[pendingChannel] || pendingChannel}`}
                hasConfigValue={() => hasConfigValue()}
                getDefaultPrice={getDefaultPrice}
                handleConfirm={handleConfirm}
                configType={configType}
                selectedConfig={data}
            />

            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: '#fff', p: 2.5, mt: 1.5 }}>
                <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5 }}>
                        {description}
                    </Typography>
                    {anyActive && (
                        <Box sx={{ mt: 1 }}>
                            <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>Price: </Typography>
                            <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>₹{price}</Typography>
                        </Box>
                    )}
                </Box>

                {CHANNELS.map((ch) => {
                    const active = isChannelActive(ch);
                    return (
                        <Box key={ch} sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1.5,
                            borderTop: `1px solid ${BORDER}`,
                        }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: NAVY }}>
                                {CHANNEL_LABELS[ch] || ch}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PaymentSegmented value={paymentModes[ch]} onChange={(m) => handlePaymentChange(ch, m)} />
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, minWidth: 40, textAlign: 'right' }}>
                                    ₹{price}
                                </Typography>
                                <Switch
                                    checked={!!active}
                                    onChange={() => onToggle(ch)}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </>
    );
};

export default ChannelConfigCard;
